import { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { logger } from 'utils/logger';

import {
  getWorksheetClosingInvestorAction,
  getWorksheetLoanFeesViewAction,
} from 'store/documents/actions';
// Concrete module, not the store/loanApplications/selectors barrel (see WorksheetDrawer's imports).
import { selectIsChattelLoanPurposeFromApplication } from 'store/loanApplications/selectors/loanApplicationCommonInfo.selectors';

import {
  buildWorksheetFeesPrefillValues,
  buildWorksheetPrefillValues,
  getPrefillFields,
  isFeesViewPrefillSource,
  isInvestorPrefillSource,
  isSameWorksheetAnswer,
  isWorksheetAnswerEmpty,
} from './utils';

/**
 * Prefills a NEW worksheet's opted-in fields (`prefill: <source>` in the field's description) with
 * loan values (LEN-194: the Dealer Options Worksheet's Loan Amount, Retail Sale Price, Sales Tax)
 * and the closing investor's Chattel max advance % (LEN-201: its Markup) and revenue sheet terms
 * (LEN-194: reserve %, spread, reserve rate, state, agreement date). Chattel loans only — for
 * any other loan purpose, a reopened (saved) worksheet, or a worksheet without opted-in fields it
 * fills nothing. A field is filled only while it is empty, and once it holds a value (prefilled or
 * typed) it is never touched again, so the user can overtype or clear it.
 *
 * Fee sources (`prefill: fee: Processing Fee`, `loan.initialEscrowDeposit` — LEN-194: the Chattel
 * Funding Worksheet's wire calculation) read the loan's fees view, fetched only for them.
 *
 * A reopened worksheet keeps following the loan (LEN-194: CIS reopened an advance worksheet after
 * re-keying the loan and it still compared against the old loan amount): once its saved answers are
 * in the form, each loan- or fee-sourced answer that no longer matches the loan is updated, with a
 * hint naming the saved value. Investor terms are left as saved.
 *
 * @param {{ loanId: string|null, applicationId: string|null, isOpen: boolean, isNewWorksheet: boolean, isSavedWorksheetLoaded: boolean, fields: Array, form: Object, setInForm: Function, loanFacts: Object|null }} params
 * @returns {Object<string, string>} the hint to show under each prefilled field, by field id
 */
export const useWorksheetPrefill = ({
  loanId,
  applicationId = null,
  isOpen,
  isNewWorksheet,
  isSavedWorksheetLoaded = false,
  fields,
  form,
  setInForm,
  loanFacts = null,
}) => {
  const dispatch = useDispatch();
  const isChattelLoan = useSelector(selectIsChattelLoanPurposeFromApplication);

  const [investor, setInvestor] = useState(null);
  const [feesView, setFeesView] = useState(null);
  // A reopened worksheet's answers updated to the loan's, by field id: the saved answer each replaced.
  const [replacedAnswers, setReplacedAnswers] = useState({});
  // Fields that already hold a value this open: never prefilled (again).
  const settledFieldIdsRef = useRef(new Set());

  const prefillFields = useMemo(() => getPrefillFields(fields), [fields]);
  const hasInvestorPrefillFields = prefillFields.some(({ source }) =>
    isInvestorPrefillSource(source),
  );
  const feesViewSources = useMemo(
    () => prefillFields.map(({ source }) => source).filter(isFeesViewPrefillSource),
    [prefillFields],
  );
  const hasFeesViewPrefillFields = !!feesViewSources.length;
  const isPrefillAvailable = isOpen && isChattelLoan && !!prefillFields.length;
  const isNewWorksheetPrefill = isPrefillAvailable && isNewWorksheet;
  const isSavedWorksheetRefresh = isPrefillAvailable && !isNewWorksheet && isSavedWorksheetLoaded;
  const isPrefillEnabled = isNewWorksheetPrefill || isSavedWorksheetRefresh;
  const prefillValues = useMemo(
    () =>
      isPrefillEnabled
        ? {
            ...buildWorksheetPrefillValues({ loanFacts, investor }),
            ...buildWorksheetFeesPrefillValues({ feesView, sources: feesViewSources }),
          }
        : {},
    [isPrefillEnabled, loanFacts, investor, feesView, feesViewSources],
  );

  useEffectSafeStrictMode(() => {
    setInvestor(null);
    setReplacedAnswers({});
    settledFieldIdsRef.current = new Set();

    if (!isNewWorksheetPrefill || !loanId || !hasInvestorPrefillFields) {
      return;
    }

    // A reopen for another loan must not apply the previous loan's late answer.
    const request = { isCancelled: false };

    dispatch(getWorksheetClosingInvestorAction({ loanId, silent: true }))
      .then(closingInvestor => {
        if (!request.isCancelled) {
          setInvestor(closingInvestor ?? null);
        }
      })
      .catch(logger.error);

    return () => {
      request.isCancelled = true;
    };
  }, [isPrefillEnabled, isNewWorksheetPrefill, loanId, hasInvestorPrefillFields, dispatch]);

  useEffectSafeStrictMode(() => {
    setFeesView(null);

    if (!isPrefillEnabled || !applicationId || !hasFeesViewPrefillFields) {
      return;
    }

    const request = { isCancelled: false };

    dispatch(getWorksheetLoanFeesViewAction({ applicationId, silent: true }))
      .then(response => {
        if (!request.isCancelled) {
          setFeesView(response ?? null);
        }
      })
      .catch(logger.error);

    return () => {
      request.isCancelled = true;
    };
  }, [isPrefillEnabled, applicationId, hasFeesViewPrefillFields, dispatch]);

  useEffectSafeStrictMode(() => {
    prefillFields.forEach(({ id, source }) => {
      const prefillValue = prefillValues[source];

      if (!prefillValue || settledFieldIdsRef.current.has(id)) {
        return;
      }

      const answer = form?.[id];

      if (isWorksheetAnswerEmpty(answer)) {
        setInForm(id, prefillValue.value, false);

        return;
      }

      settledFieldIdsRef.current.add(id);

      if (
        isSavedWorksheetRefresh &&
        !isInvestorPrefillSource(source) &&
        !isSameWorksheetAnswer(answer, prefillValue.value)
      ) {
        setReplacedAnswers(replaced => ({ ...replaced, [id]: answer }));
        setInForm(id, prefillValue.value, false);
      }
    });
  }, [prefillValues, prefillFields, form, setInForm, isSavedWorksheetRefresh]);

  return useMemo(
    () =>
      _.fromPairs(
        prefillFields
          .filter(({ source }) => prefillValues[source])
          .map(({ id, source }) => [
            id,
            _.has(replacedAnswers, id)
              ? `${prefillValues[source].hint} (was ${replacedAnswers[id]} when saved)`
              : prefillValues[source].hint,
          ]),
      ),
    [prefillValues, prefillFields, replacedAnswers],
  );
};
